import { useNavigate } from 'react-router-dom';
import { Box, Chip, ChipProps, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { EmptyState } from '../../components/EmptyState';
import { CounselorDashboard } from '../../types/dashboard';
import { PaymentStatus } from '../../types/payment';

const STATUS_COLOR: Record<PaymentStatus, ChipProps['color']> = {
  PENDING: 'default',
  APPROVED: 'success',
  REJECTED: 'error',
  REVERSED: 'warning',
};

interface MyStudentPaymentsCardProps {
  data?: CounselorDashboard['recentPayments'];
  loading?: boolean;
}

/** Counselor Dashboard's "My Student Payments" — only payments for this
 * Counselor's own students (spec §9), sourced from GET /dashboard/counselor
 * rather than the general, branch-only /payments listing. */
export const MyStudentPaymentsCard = ({ data, loading }: MyStudentPaymentsCardProps) => {
  const navigate = useNavigate();
  const payments = data ?? [];

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">My Student Payments</Typography>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/payments')}
        >
          View all
        </Typography>
      </Stack>
      {payments.length === 0 ? (
        <EmptyState loading={loading} message="No payments yet" />
      ) : (
        <List dense disablePadding>
          {payments.map((payment) => (
            <ListItemButton key={payment.id} onClick={() => navigate('/payments')} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText
                primary={`${payment.student.firstName} ${payment.student.lastName}`}
                secondary={`${payment.paymentNumber} · ${payment.currency.symbol ?? payment.currency.code}${payment.amount}`}
              />
              <Box>
                <Chip size="small" label={payment.status} color={STATUS_COLOR[payment.status as PaymentStatus]} />
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
